/* Import React modules */
import React, { useEffect, useState } from "react";
/* Import other node modules */
import ContentstackAppSdk from "@contentstack/app-sdk";
import { AsyncSelect, Button, Tooltip } from "@contentstack/venus-components";
import { TypeSDKData, TypeWarningtext } from "../../common/types";

/* Import our modules */
/* Import node module CSS */
/* Import our CSS */
import "./styles.scss";
import services from "../../services";
import { getTokenFromUrl, setAxiosInterceptors } from "../../interceptor";
import localeTexts from "../../common/locale/en-us";
import WarningMessage from "../../components/WarningMessage";

const CustomField: React.FC = function () {
  // error tracking hooks
  const authTokenFromUrl = getTokenFromUrl();
  const [state, setState] = useState<TypeSDKData>({
    config: {},
    location: {},
    appSdkInitialized: false,
  });

  const [value, setValue] = useState<any>(null);
  const [fromLabel, setFromLabel] = useState<any>([]);
  const [baseUrl, setBaseUrl] = useState("");
  const [count, setCount] = useState(0);

  const [isInvalidCredentials, setIsInvalidCredentials] =
    useState<TypeWarningtext>({
      error: false,
      data: localeTexts.Warnings.invalidCredentials,
    });

  const getBaseUrl = async () => {
    const url = await services?.getBaseUrl();
    if (!url?.error) {
      if (url?.data === "") {
        console.error(localeTexts.Warnings.invalidCredentials);
        setIsInvalidCredentials({
          error: true,
          data: localeTexts.Warnings.invalidCredentials,
        });
      }
      setBaseUrl(url?.data);
    }
  };

  const filterForm = (formObj: any) => {
    const { iscustomFields, formKeys } = state?.config || {};
    if (!iscustomFields) {
      return formObj;
    }
    const filteredItem: any = {};
    formKeys?.forEach((key: any) => {
      filteredItem[key?.value] = formObj?.[key?.value];
    });
    return filteredItem;
  };

  useEffect(() => {
    if (value) {
      const labelToFind = value?.value;
      if (!labelToFind) {
        return;
      }
      const matchedForm =
        fromLabel?.find((form: any) => form?.name === labelToFind) ?? null;
      if (matchedForm) {
        const finalForm = filterForm(matchedForm);
        state?.location?.CustomField?.field?.setData({ form: finalForm });
      }
    } else {
      state?.location?.CustomField?.field?.setData({ form: value });
    }
  }, [value, state]);

  useEffect(() => {
    ContentstackAppSdk.init()
      .then(async (appSdk: any) => {
        /* eslint no-underscore-dangle: 0 */
        // eslint-disable-next-line  @typescript-eslint/naming-convention
        const { api_key } = appSdk?.stack?._data || {};
        setAxiosInterceptors(authTokenFromUrl, api_key);

        const config = await appSdk?.getConfig();
        const initialData = appSdk?.location?.CustomField?.field?.getData();

        appSdk?.location?.CustomField?.frame?.updateHeight(170);
        if (initialData?.form && Object.keys(initialData?.form).length > 0) {
          const getFormById = await services?.getFormById(
            initialData?.form?._id
          );
          initialData.form.label = initialData?.form?.title;
          initialData.form.value = initialData?.form?.name;

          if (!getFormById?.error) {
            setValue(initialData?.form);
            setFromLabel(getFormById?.data);
          }
        }
        await getBaseUrl();
        setState({
          config,
          location: appSdk?.location,
          appSdkInitialized: true,
        });
      })
      .catch((error) => {
        console.error("appSdk initialization error", error);
      });
  }, []);

  const handleValueUpdate = (data: any) => {
    setValue(data);
  };
  const handleClick = () => {
    const selectedForm = fromLabel?.find(
      (obj: any) => obj?.name === value?.value
    );
    const previewUrl = `${process.env.REACT_APP_FORMIO_PREVIEW_URL}${baseUrl}/project/${selectedForm?.project}/form/${selectedForm?._id}`;
    if (value) {
      window.open(previewUrl);
    }
  };

  const loadMoreOptions: any = async ({ search }: { search: string }) => {
    if (search) {
      const searchbyFormName = await services?.getForm({
        skip: 0,
        searchQuery: search,
        task: "search",
      });
      if (searchbyFormName?.error) {
        console.error(localeTexts?.errors?.searchByName);
        setIsInvalidCredentials({
          error: true,
          data: localeTexts?.errors?.searchByName,
        });
        return {
          options: [],
          hasMore: false,
        };
      }

      let parsedData: any;
      if (typeof searchbyFormName?.data === "string") {
        parsedData = JSON.parse(searchbyFormName?.data);
      } else {
        parsedData = searchbyFormName?.data;
      }

      const parsedFormsData = parsedData?.[1]?.data;
      if (parsedFormsData?.length) {
        setFromLabel((prevForms: any) => [...prevForms, ...parsedFormsData]);
        const newResult = parsedFormsData?.map((obj: any) => ({
          id: obj?._id,
          label: (
            <Tooltip
              content={obj?.title}
              position="top"
              type="primary"
              variantType="dark"
            >
              <div className="options-div">
                <p className="options">{obj?.title}</p>
              </div>
            </Tooltip>
          ),
          searchableLabel: obj?.title,
          value: obj?.name,
        }));

        return {
          options: newResult,
          hasMore: parsedFormsData?.length === 20,
        };
      }
      return {
        options: [],
        hasMore: false,
      };
    }

    const skip = 20 * count;
    try {
      const formData = await services?.getForm({ skip });

      if (formData?.error) {
        console.error(localeTexts.Warnings.invalidCredentials);
        setIsInvalidCredentials({
          error: formData.error,
          data: localeTexts.Warnings.invalidCredentials,
        });
        return {
          options: [],
          hasMore: false,
        };
      }
      let parsedData: any;
      if (typeof formData?.data === "string") {
        parsedData = JSON.parse(formData?.data);
      } else {
        parsedData = formData?.data;
      }
      const parsedFormsData = parsedData?.[1]?.data;

      if (parsedFormsData?.length) {
        setFromLabel((prevForms: any) => [...prevForms, ...parsedFormsData]);

        // eslint-disable-next-line
        const newResult = parsedFormsData?.map((obj: any) => ({
          id: obj?._id,
          label: (
            <Tooltip
              content={obj?.title}
              position="top"
              type="primary"
              variantType="dark"
            >
              <div className="options-div">
                <p className="options">{obj?.title}</p>
              </div>
            </Tooltip>
          ),
          searchableLabel: obj?.title,
          value: obj?.name,
        }));

        setCount(count + 1);
        return {
          options: newResult,
          hasMore: parsedFormsData?.length === 20,
        };
      }
      return {
        options: [],
        hasMore: false,
      };
    } catch (error) {
      console.error(error);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  function renderCustomField() {
    if (isInvalidCredentials.error) {
      return <WarningMessage content={isInvalidCredentials.data} />;
    }

    if (state.appSdkInitialized) {
      return (
        <div className="field-extension">
          <div className="field-extension-wrapper">
            <div className="render-items">
              <AsyncSelect
                debounceTimeout={0}
                isClearable
                isSearchable
                limit={20}
                loadMoreOptions={loadMoreOptions}
                onChange={handleValueUpdate}
                placeholder={localeTexts?.customField?.select?.label}
                value={value}
                width="200px"
                defaultOptions={false}
                version="v2"
              />
            </div>
          </div>
          <div className="previewButtonContainer">
            <Button
              onClick={handleClick}
              buttonType="primary"
              iconAlignment="left"
              icon="ViewWhite"
              className="preview-button"
              disabled={!value}
            >
              {localeTexts.customField.button}
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  return renderCustomField();
};

export default CustomField;
